import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc,
  getDoc,
  setDoc,
} from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { alpha } from '@mui/material/styles';

import { Editor } from '../../ui-components/editor';

// ----------------------------------------------------------------------

interface TemplateEmail {
  id: string;
  name: string;
  subject: string;
  html: string;
  sms: string;
  confirmation: string;
  modified?: Date;
}

export default function NotificationTemplatesPage() {
  const [templates, setTemplates] = useState<TemplateEmail[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateEmail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [dayOfMessageEnabled, setDayOfMessageEnabled] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const templatesRef = collection(db, 'templateEmails');
      const snapshot = await getDocs(templatesRef);
      
      const templateData: TemplateEmail[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        templateData.push({
          id: docSnap.id,
          ...data,
          // Convert Firestore timestamp to Date if it exists
          modified: data.modified ? data.modified.toDate() : undefined,
        } as TemplateEmail);
      });
      
      setTemplates(templateData);
      // Only set initial template if none is selected
      setSelectedTemplate(prev => prev || (templateData.length > 0 ? templateData[0] : null));
    } catch (error) {
      console.error('Error fetching templates:', error);
      setMessage({ type: 'error', text: 'Failed to load templates' });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDayOfMessageConfig = useCallback(async () => {
    try {
      setLoadingConfig(true);
      const configRef = doc(db, 'config', 'dayOfMessage');
      const configSnap = await getDoc(configRef);
      
      if (configSnap.exists()) {
        const data = configSnap.data();
        setDayOfMessageEnabled(data?.enabled ?? false);
      } else {
        setDayOfMessageEnabled(false);
      }
    } catch (error) {
      console.error('Error fetching dayOfMessage config:', error);
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Load config when "Day-of Message" template is selected
  useEffect(() => {
    if (selectedTemplate?.name === 'Day-of Message') {
      fetchDayOfMessageConfig();
    }
  }, [selectedTemplate?.name, fetchDayOfMessageConfig]);

  const handleSave = async () => {
    if (!selectedTemplate) return;

    try {
      setSaving(true);
      const templateRef = doc(db, 'templateEmails', selectedTemplate.id);
      
      // Filter out undefined values to prevent Firestore errors
      const updateData: Record<string, any> = {
        modified: new Date()
      };
      
      if (selectedTemplate.subject !== undefined) updateData.subject = selectedTemplate.subject;
      if (selectedTemplate.html !== undefined) updateData.html = selectedTemplate.html;
      if (selectedTemplate.sms !== undefined) updateData.sms = selectedTemplate.sms;
      if (selectedTemplate.confirmation !== undefined) updateData.confirmation = selectedTemplate.confirmation;
      
      await updateDoc(templateRef, updateData);

      // If this is the Day-of Message template, also save the config
      if (selectedTemplate.name === 'Day-of Message') {
        const configRef = doc(db, 'config', 'dayOfMessage');
        await setDoc(configRef, {
          enabled: dayOfMessageEnabled
        }, { merge: true });
      }

      setMessage({ type: 'success', text: 'Template saved successfully' });
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving template:', error);
      setMessage({ type: 'error', text: 'Failed to save template' });
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = useCallback((field: keyof TemplateEmail, value: string) => {
    setSelectedTemplate(prev => {
      if (!prev) return null;
      return {
        ...prev,
        [field]: value
      };
    });
  }, []);

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh' 
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 120px)' }}>
      {/* Left Sidebar - Template List */}
      <Paper 
        sx={{ 
          width: 300, 
          borderRadius: 0,
          borderRight: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Templates</Typography>
        </Box>
        
        <List sx={{ p: 0, overflow: 'auto', flex: 1 }}>
          {templates.map((template) => (
            <ListItem key={template.id} disablePadding>
              <ListItemButton
                selected={selectedTemplate?.id === template.id}
                onClick={() => setSelectedTemplate(template)}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.15),
                    },
                  },
                }}
              >
                <ListItemText 
                  primary={template.name}
                  secondary={template.modified ? 
                    `Modified ${template.modified.toLocaleDateString()}` : 
                    'Never modified'
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Right Panel - Template Editor */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {selectedTemplate ? (
          <>
            {/* Header */}
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h5" gutterBottom>
                    {selectedTemplate.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Edit the template content below
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} /> : null}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Stack>
              
              {message && (
                <Alert 
                  severity={message.type} 
                  sx={{ mt: 2 }}
                  onClose={() => setMessage(null)}
                >
                  {message.text}
                </Alert>
              )}
            </Box>

            {/* Form Fields */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              <Stack spacing={3}>
                {/* Name (disabled) */}
                <TextField
                  label="Template Name"
                  value={selectedTemplate.name}
                  disabled
                  fullWidth
                />

                {/* Day-of Message Template - Special handling */}
                {selectedTemplate.name === 'Day-of Message' ? (
                  <>
                    {/* Enable Checkbox */}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={dayOfMessageEnabled}
                          onChange={(e) => setDayOfMessageEnabled(e.target.checked)}
                          disabled={loadingConfig}
                        />
                      }
                      label="Enable Day-of Message"
                    />

                    {/* SMS Only */}
                    <TextField
                      key={`sms-${selectedTemplate.id}`}
                      label="SMS Message"
                      value={selectedTemplate.sms}
                      onChange={(e) => handleFieldChange('sms', e.target.value)}
                      multiline
                      rows={4}
                      fullWidth
                      placeholder="Enter SMS message content..."
                      helperText="Keep SMS messages concise (under 160 characters recommended)"
                    />
                  </>
                ) : (
                  <>
                    {/* Subject */}
                    <TextField
                      key={`subject-${selectedTemplate.id}`}
                      label="Email Subject"
                      value={selectedTemplate.subject}
                      onChange={(e) => handleFieldChange('subject', e.target.value)}
                      fullWidth
                      placeholder="Enter email subject line..."
                    />

                    {/* HTML Content */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        HTML Content
                      </Typography>
                      <Editor
                        key={`html-${selectedTemplate.id}`}
                        value={selectedTemplate.html}
                        onChange={(value: string) => handleFieldChange('html', value)}
                        placeholder="Enter HTML email content..."
                        sx={{ minHeight: 200 }}
                      />
                    </Box>

                    {/* SMS */}
                    <TextField
                      key={`sms-${selectedTemplate.id}`}
                      label="SMS Message"
                      value={selectedTemplate.sms}
                      onChange={(e) => handleFieldChange('sms', e.target.value)}
                      multiline
                      rows={4}
                      fullWidth
                      placeholder="Enter SMS message content..."
                      helperText="Keep SMS messages concise (under 160 characters recommended)"
                    />

                    {/* Confirmation */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Confirmation Content
                      </Typography>
                      <Editor
                        key={`confirmation-${selectedTemplate.id}`}
                        value={selectedTemplate.confirmation}
                        onChange={(value: string) => handleFieldChange('confirmation', value)}
                        placeholder="Enter confirmation content..."
                        sx={{ minHeight: 300 }}
                      />
                    </Box>
                  </>
                )}
              </Stack>
            </Box>
          </>
        ) : (
          <Box 
            sx={{ 
              flex: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Select a template to edit
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
